/** @jsxImportSource ../i18n */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Check, Eye, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { fetchWatchlist, addToWatchlist, removeFromWatchlist } from '../store/slices/portfolioSlice';
import { useToast } from '../contexts/ToastContext';
import api from '../services/api';
import { Badge, Banner, Button, Card, CardHeader, EmptyState, Field, IconButton, SearchInput, Spinner, SymbolMark } from './ui';

const symbolKey = (symbol = '') => {
  const [ticker, mic = ''] = symbol.trim().toUpperCase().split(':');
  return `${ticker.split('_')[0]}:${mic}`;
};

export const isEtf = (item) => (item.AssetType || item.assetType) === 'Etf';

const Watchlist = () => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { watchlist, watchlistLoading, watchlistError, positions, saxoConnected } = useSelector(state => state.portfolio);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [pendingSymbol, setPendingSymbol] = useState(null);
  const [mutationError, setMutationError] = useState('');

  useEffect(() => {
    const term = query.trim();
    setResults([]);
    setSearchError('');
    if (!saxoConnected || term.length < 2) {
      setSearching(false);
      return undefined;
    }
    let active = true;
    const controller = new AbortController();
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const response = await api.get('/market/search/eu', { params: { q: term }, signal: controller.signal });
        if (active) setResults((Array.isArray(response.data) ? response.data : []).filter(item => item.Symbol || item.symbol));
      } catch (error) {
        if (active) setSearchError('La recherche est indisponible. Vérifiez la connexion Saxo et réessayez.');
      } finally {
        if (active) setSearching(false);
      }
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, saxoConnected]);

  const addStock = async (item) => {
    const symbol = item.Symbol || item.symbol;
    setPendingSymbol(symbol);
    setMutationError('');
    try {
      await dispatch(addToWatchlist({ symbol, description: item.Description || item.description || '' })).unwrap();
      setQuery('');
      toast.success(`${symbol} ajouté à la watchlist`);
    } catch (error) {
      setMutationError(typeof error === 'string' ? error : "Impossible d'ajouter ce titre.");
    } finally {
      setPendingSymbol(null);
    }
  };

  const removeStock = async (symbol) => {
    setPendingSymbol(symbol);
    setMutationError('');
    try {
      await dispatch(removeFromWatchlist(symbol)).unwrap();
      toast.success(`${symbol} retiré de la watchlist`);
    } catch (error) {
      setMutationError(typeof error === 'string' ? error : 'Impossible de retirer ce titre.');
    } finally {
      setPendingSymbol(null);
    }
  };

  const busy = pendingSymbol !== null || watchlistLoading;
  const noResults = !searching && !searchError && saxoConnected && query.trim().length >= 2 && results.length === 0;

  return (
    <Card>
      <CardHeader
        icon={Eye}
        title="Actions et ETF à surveiller"
        description="Ces titres sont analysés individuellement dans une section dédiée du prochain rapport quotidien par email, sans modifier les totaux de votre portefeuille."
        actions={(
          <IconButton
            icon={RefreshCw}
            label="Actualiser la watchlist"
            variant="secondary"
            onClick={() => dispatch(fetchWatchlist())}
            disabled={watchlistLoading || pendingSymbol !== null}
            iconClassName={watchlistLoading ? 'animate-spin' : undefined}
          />
        )}
      />
      <div className="space-y-6">
        {(watchlistError || mutationError) && (
          <div className="space-y-2">
            {watchlistError && <Banner tone="loss" compact role="alert">{watchlistError}</Banner>}
            {mutationError && <Banner tone="loss" compact role="alert">{mutationError}</Banner>}
          </div>
        )}
        <div>
          <Field label="Rechercher une action ou un ETF européen" htmlFor="watchlist-search" hint="L'éligibilité au PEA doit être vérifiée avant tout achat.">
            <SearchInput
              id="watchlist-search"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onClear={() => setQuery('')}
              disabled={!saxoConnected}
              placeholder="Nom ou symbole : ASML, TotalEnergies, LQQ…"
            />
          </Field>
          {!saxoConnected && (
            <Banner tone="warn" compact className="mt-3">
              Connectez Saxo depuis le tableau de bord pour rechercher de nouveaux titres. Vous pouvez toujours consulter et retirer les titres enregistrés.
            </Banner>
          )}
          {searching && <Spinner label="Recherche en cours…" className="mt-3" />}
          {searchError && <Banner tone="loss" compact role="alert" className="mt-3">{searchError}</Banner>}
          {noResults && <p role="status" className="mt-3 text-sm text-fg-muted">Aucun titre trouvé.</p>}
          {results.length > 0 && (
            <ul className="mt-3 max-h-72 divide-y divide-line overflow-y-auto rounded-control border border-line bg-surface-2/40" aria-label="Résultats de recherche">
              {results.map((item, index) => {
                const symbol = item.Symbol || item.symbol;
                const alreadyAdded = watchlist.some(stock => symbolKey(stock.symbol) === symbolKey(symbol));
                return (
                  <li key={`${symbol}-${index}`} className="flex items-center justify-between gap-3 px-3 py-2.5">
                    <div className="flex min-w-0 items-center gap-3">
                      <SymbolMark symbol={symbol} size="sm" />
                      <div className="min-w-0">
                        <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-fg">
                          <span className="break-words">{symbol}</span>
                          {isEtf(item) && <Badge tone="gold" data-testid="etf-badge">ETF</Badge>}
                        </p>
                        <p className="break-words text-xs text-fg-subtle">{item.Description || item.description}</p>
                      </div>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      leadingIcon={alreadyAdded ? Check : Plus}
                      loading={pendingSymbol === symbol}
                      onClick={() => addStock(item)}
                      disabled={alreadyAdded || busy}
                      aria-label={`Ajouter ${symbol} à la watchlist`}
                    >
                      {alreadyAdded ? 'Déjà ajouté' : 'Ajouter'}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div>
          <h3 className="eyebrow mb-2">Watchlist ({watchlist.length})</h3>
          {watchlistLoading && <Spinner label="Chargement de la watchlist…" className="py-3" />}
          {!watchlistLoading && !watchlistError && watchlist.length === 0 && (
            <EmptyState icon={Eye} description="Votre watchlist est vide. Recherchez une action ou un ETF pour commencer à le surveiller." />
          )}
          <ul className="divide-y divide-line" aria-label="Titres surveillés">
            {watchlist.map(item => {
              const held = positions.some(position => symbolKey(position.symbol) === symbolKey(item.symbol));
              return (
                <li key={item.id || item.symbol} className="flex items-center justify-between gap-3 py-3.5">
                  <div className="flex min-w-0 items-center gap-3">
                    <SymbolMark symbol={item.symbol} />
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <Link to={`/stock/${encodeURIComponent(item.symbol)}`} className="break-words font-medium text-fg transition-colors hover:text-gold-200">{item.symbol}</Link>
                        {held && <Badge>Également en portefeuille</Badge>}
                      </div>
                      {item.description && <p className="break-words text-sm text-fg-subtle">{item.description}</p>}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    leadingIcon={Trash2}
                    loading={pendingSymbol === item.symbol}
                    onClick={() => removeStock(item.symbol)}
                    disabled={busy}
                    aria-label={`Retirer ${item.symbol} de la watchlist`}
                  >
                    <span className="hidden sm:inline">Retirer</span>
                  </Button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </Card>
  );
};

export default Watchlist;
